import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { partnerApi, type PartnerBalance } from '@/lib/partnerApi';

interface PartnerContextType {
  isAuthenticated: boolean;
  balance: PartnerBalance | null;
  keyLast4: string | null;
  isLoading: boolean;
  login: (apiKey: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshBalance: () => Promise<void>;
}

const PartnerContext = createContext<PartnerContextType | undefined>(undefined);

export function PartnerProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [balance, setBalance] = useState<PartnerBalance | null>(null);
  const [keyLast4, setKeyLast4] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // При mount проверяем cookie-сессию через /me
  useEffect(() => {
    (async () => {
      const me = await partnerApi.me();
      if (me?.authenticated) {
        setIsAuthenticated(true);
        setKeyLast4(me.key_last4);
        try {
          const d = await partnerApi.getDashboard();
          setBalance(d.balance);
        } catch {
          /* игнорируем — Dashboard сам покажет ошибку */
        }
      } else {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    })();
  }, []);

  const login = async (apiKey: string) => {
    setIsLoading(true);
    try {
      const data = await partnerApi.login(apiKey);
      setBalance(data.balance);
      setKeyLast4(data.partner.key_last4);
      setIsAuthenticated(true);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await partnerApi.logout();
    setIsAuthenticated(false);
    setBalance(null);
    setKeyLast4(null);
  };

  const refreshBalance = async () => {
    try {
      const d = await partnerApi.getDashboard();
      setBalance(d.balance);
    } catch {
      /* no-op */
    }
  };

  return (
    <PartnerContext.Provider value={{ isAuthenticated, balance, keyLast4, isLoading, login, logout, refreshBalance }}>
      {children}
    </PartnerContext.Provider>
  );
}

export function usePartner() {
  const ctx = useContext(PartnerContext);
  if (!ctx) throw new Error('usePartner must be used within PartnerProvider');
  return ctx;
}
